import styled from 'styled-components';
import { IThemeProps } from 'store/ducks/globalTypes';
import { themesList } from 'constants/themesList';
import {
  RiskSeverityLevelProps,
  SEVERITY_COLOR_MAP,
  DraggableElementProps,
  PROCESS_CRITICALITY_COLOR_MAP,
  SeverityName
} from 'store/ducks/InternalControls/types';
import CustomSelect from 'components/CustomSelect';

export const Wrapper = styled.div<IThemeProps>`
  padding: 24px;
  font-family: 'Inter', sans-serif;
  background: ${(props) => {
    const currentTheme =
      themesList[(props.scheme || 'mainTheme') as keyof typeof themesList];
    return (
      currentTheme?.colorBgLayout || themesList.deepBlueTheme.colorBgLayout
    );
  }};
  min-height: 100vh;
`;

export const DndHintBanner = styled.div<IThemeProps>`
  width: 100%;
  background: ${({ scheme }) => {
    const t = themesList[(scheme || 'mainTheme') as keyof typeof themesList];
    return `linear-gradient(135deg, ${t?.colorPrimary}18, ${t?.colorPrimary}08)`;
  }};
  border: 1px dashed
    ${({ scheme }) => {
      const t = themesList[(scheme || 'mainTheme') as keyof typeof themesList];
      return `${t?.colorPrimary}66`;
    }};
  border-radius: 12px;
  padding: 14px 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    padding: 10px 16px;
  }
`;

export const DndHintTitle = styled.span<IThemeProps>`
  font-weight: 600;
  font-size: 14px;
  color: ${({ scheme }) => {
    const t =
      themesList[(scheme || 'deepBlueTheme') as keyof typeof themesList];
    return t?.colorPrimary || '#053170';
  }};
  white-space: nowrap;
`;

export const DndHintDesc = styled.span<IThemeProps>`
  font-size: 13px;
  color: ${({ scheme }) => {
    const t =
      themesList[(scheme || 'deepBlueTheme') as keyof typeof themesList];
    return t?.colorPrimary || '#053170';
  }};

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

export const DropBox = styled.div`
  display: flex;
  gap: 32px;
  margin-top: 24px;
  flex-wrap: nowrap;
  justify-content: center;
  overflow-x: auto;
`;

export const ColumnBox = styled.div<{
  isOver?: boolean;
  token: any;
}>`
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(6px);

  border-radius: 18px;
  padding: 24px;

  border: 1px solid ${({ isOver }) => (isOver ? '#1677ff' : 'rgba(0,0,0,0.06)')};

  width: 589px;
  height: 100vh;
  overflow-y: auto;

  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);

  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  .ant-input {
    margin: 0 0 8px 0;
    border: 2px solid ${({ token }) => token.colorPrimary};
  }

  .ant-select {
    width: 100%;
    margin-bottom: 8px;
    border: 2px solid ${({ token }) => token.colorPrimary};
    border-radius: 10px;
  }

  @media (max-width: 1024px) {
    width: 100%;
    height: 400px;
  }
`;

export const ColumnTitle = styled.h3`
  font-weight: 600;
  letter-spacing: -0.01em;
  margin-bottom: 20px;
  font-size: 18px;
  color: #111827;
  text-align: center;
`;

export const ItemBox = styled.div<{
  isDragging?: boolean;
  disabled?: boolean;
  severity?: SeverityName;
}>`
  padding: 14px 18px;
  background: ${({ disabled, severity }) =>
    disabled ? '#f9fafb' : severity ? SEVERITY_COLOR_MAP[severity] : '#ffffff'};

  border: 2px solid
    ${({ isDragging, disabled }) =>
      disabled ? '#d1d5db' : isDragging ? '#1677ff' : '#e5e7eb'};

  border-radius: 14px;

  cursor: ${({ disabled, isDragging }) =>
    disabled ? 'not-allowed' : isDragging ? 'grabbing' : 'grab'};

  opacity: ${({ disabled, isDragging }) =>
    disabled ? 0.5 : isDragging ? 0.65 : 1};

  box-shadow: ${({ disabled, isDragging }) =>
    disabled
      ? 'none'
      : isDragging
      ? '0 16px 32px rgba(22,119,255,0.25)'
      : '0 4px 12px rgba(0,0,0,0.08)'};

  transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s;

  font-weight: 500;
  color: ${({ disabled, severity }) =>
    disabled ? '#6b7280' : severity ? '#ffffff !important' : '#111827'};
  margin-bottom: 12px;

  * {
    color: inherit !important;
  }

  ${({ disabled }) =>
    !disabled &&
    `
      &:hover {
        transform: translateY(-2px);
        border-color: #1677ff;
      }

      &:active {
        cursor: grabbing;
      }
    `}
`;

export const BprItemContainer = styled.div<{
  isOver?: boolean;
  criticality?: string;
}>`
  padding: 18px;
  margin-bottom: 14px;

  border: 2px solid ${({ isOver }) => (isOver ? '#60d466' : '#d1d5db')};

  background: ${({ isOver, criticality }) =>
    isOver
      ? '#60d466'
      : criticality
      ? PROCESS_CRITICALITY_COLOR_MAP[criticality]
      : '#fafafa'};

  border-radius: 14px;

  box-shadow: ${({ isOver }) =>
    isOver ? '0 0 0 2px rgba(82,196,26,0.25)' : 'none'};

  transition: all 0.2s ease;
`;

export const LinkedItem = styled.div<{
  isOver: boolean;
  severity?: SeverityName | null;
}>`
  border: 1px solid ${({ isOver }) => (isOver ? '#60d466' : '#e5e7eb')};
  padding: 14px;
  border-radius: 12px;

  /* use severity color, fallback to white */
  background: ${({ isOver, severity }) =>
    isOver ? '#60d466' : severity ? SEVERITY_COLOR_MAP[severity] : '#ffffff'};

  color: ${({ severity }) => (severity ? '#ffffff !important' : '#000000')};

  margin-top: 10px;
  margin-bottom: 10px;

  transition: all 0.2s ease;

  * {
    color: inherit !important;
  }
`;

export const NestedControl = styled(LinkedItem)`
  margin-left: 24px;
  color: #000000 !important;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: -16px;
    top: 12px;
    bottom: 12px;

    width: 1px;
    background: repeating-linear-gradient(
      to bottom,
      rgba(37, 99, 235, 0.35),
      rgba(37, 99, 235, 0.35) 4px,
      transparent 4px,
      transparent 8px
    );
  }

  * {
    color: #000000 !important;
  }
`;

export const DeleteButton = styled.span`
  cursor: pointer;
  color: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
    transform: scale(1.1);
  }
`;

export const DraggableElement = styled.div<DraggableElementProps>`
  margin: 8px 0;
  padding: 14px 20px;
  cursor: grab;
  opacity: ${({ isDragging }) => (isDragging ? 0.5 : 1)};
  background: linear-gradient(135deg, #6c63ff, #4f46e5);
  color: white;
  font-weight: 600;
  border-radius: 12px;
  text-align: center;
  user-select: none;
  box-shadow: ${({ isDragging }) =>
    isDragging ? '0 12px 24px rgba(0,0,0,0.2)' : '0 2px 6px rgba(0,0,0,0.1)'};
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-3px) scale(1.02);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }
`;

export const TooltipTitle = styled('span')`
  color: #000000;
  font-weight: 500;
`;

export const RiskSeverityLevel = styled('div')<RiskSeverityLevelProps>(
  ({ severity }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderRadius: 6,
    padding: 4,
    '> div': {
      width: '100%'
    },
    '> div > div': {
      backgroundColor: severity ? SEVERITY_COLOR_MAP[severity] : undefined
    },
    '> span': {
      backgroundColor: severity ? SEVERITY_COLOR_MAP[severity] : undefined
    }
  })
);

export const SeverityBadge = styled('span')<{ severity?: string }>`
  align-self: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #fff;
  background-color: ${({ severity }) =>
    severity ? SEVERITY_COLOR_MAP[severity] : 'transparent'};
`;

export const ProcessCriticalityBadge = styled('span')<{ criticality?: string }>`
  display: block;
  margin-bottom: 4px;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #fff;
  background-color: ${({ criticality }) =>
    criticality ? PROCESS_CRITICALITY_COLOR_MAP[criticality] : 'transparent'};
`;

export const ProcessCriticalityLevel = styled('div')<RiskSeverityLevelProps>(
  ({ severity }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderRadius: 6,
    padding: 4,
    '> div': {
      backgroundColor: severity
        ? PROCESS_CRITICALITY_COLOR_MAP[severity]
        : undefined,
      width: '100%'
    }
  })
);

export const CustomSelectDropdown = styled(CustomSelect)`
  .ant-select-selector {
    min-width: 100% !important;
  }
`;
